import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import { get, noop } from 'lodash';
import { makeResourceKeys } from 'requests/resources/makeResources/makeResourcesKeys';

import { useLiveRef } from 'ui-utils';
import useSocketEventsDebounced from 'hooks/useSocketEventsDebounced';

type SubscriptionCallback = (eventStack: any[]) => void;

type SubscriptionCallbackStack = Set<SubscriptionCallback>;

const isDev = process.env.NODE_ENV !== 'production';

function logQueryInvalidation(key: unknown) {
  // eslint-disable-next-line no-console
  if (isDev) console.log('query invalidated:', key);
}

export function makeEntitySubscriptionContext(entity: string, events: string[]) {
  const SubscriptionContext = React.createContext({
    hookRegisteredCallbackRef: { current: new Set() } as React.MutableRefObject<SubscriptionCallbackStack>,
    add: noop,
    remove: noop,
  });

  function useEntitySubsciption(subscriptionCallback?: SubscriptionCallback) {
    const subscription = useContext(SubscriptionContext);
    const $subscriptionCallback = useLiveRef(subscriptionCallback);

    useEffect(() => {
      const safeSubscriptionCallback = (eventStack: any[]) =>
        $subscriptionCallback.current && $subscriptionCallback.current(eventStack);

      subscription.hookRegisteredCallbackRef.current.add(safeSubscriptionCallback);
      return () => {
        subscription.hookRegisteredCallbackRef.current.delete(safeSubscriptionCallback);
      };
    }, [$subscriptionCallback, subscription.hookRegisteredCallbackRef]);

    useEffect(() => {
      subscription.add();
      return () => {
        subscription.remove();
      };
    }, [subscription]);
  }

  function ReactQueryEntitySubscription({ children }: { children: React.ReactNode }) {
    const [subscribersCount, setSubscribersCount] = useState(0);
    const hookRegisteredCallbackRef = useRef<SubscriptionCallbackStack>(new Set());
    const queryClient = useQueryClient();

    const entityKeys = makeResourceKeys(entity);

    const $events = subscribersCount ? events : [];

    // TODO most model updates don't come from socket events. We need to handle changes from actions as well
    useSocketEventsDebounced($events, (eventStack) => {
      // Call callbacks registered in any subscription hooks
      hookRegisteredCallbackRef.current.forEach((subscriptionCallback) => subscriptionCallback(eventStack));
      // Invalidate single entity cache and all lists
      eventStack.forEach((data) => {
        const id = get(data, '_id', data);
        if (typeof id === 'string') {
          queryClient.invalidateQueries(entityKeys.$entityId(id));
          logQueryInvalidation(entityKeys.$entityId(id));
        }
      });
      queryClient.invalidateQueries(entityKeys.$many());
      logQueryInvalidation(entityKeys.$many());
    });

    const subscription = useMemo(
      () => ({
        hookRegisteredCallbackRef,
        add: () => setSubscribersCount((subscribers) => subscribers + 1),
        remove: () => setSubscribersCount((subscribers) => (subscribers > 0 ? subscribers - 1 : subscribers)),
      }),
      [],
    );

    return <SubscriptionContext.Provider value={subscription}>{children}</SubscriptionContext.Provider>;
  }

  return [ReactQueryEntitySubscription, useEntitySubsciption] as const;
}
